import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, Pause, Play, Share2, SkipForward, Volume2, VolumeX, Disc3 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { usePlayer } from "@/context/PlayerContext";
import { API, mediaUrl } from "@/lib/api";
import { Equalizer, LiveBadge } from "@/components/Equalizer";

const likedKey = "rp_liked";

function LikeShare({ track }) {
  const [liked, setLiked] = useState(() => JSON.parse(localStorage.getItem(likedKey) || "[]"));
  const isLiked = liked.includes(track.id);
  const like = async () => {
    if (isLiked) return;
    await axios.post(`${API}/tracks/${track.id}/like`).catch(() => {});
    const next = [...liked, track.id];
    localStorage.setItem(likedKey, JSON.stringify(next));
    setLiked(next);
  };
  const share = async () => {
    const url = track.artist_id ? `${window.location.origin}/artista/${track.artist_id}` : window.location.origin;
    const text = `Escuchando "${track.title}" de ${track.artist_name} en RARA PERLA`;
    if (navigator.share) { navigator.share({ title: "RARA PERLA", text, url }).catch(() => {}); return; }
    await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
    toast.success("Enlace copiado");
  };
  return (
    <div className="flex items-center gap-1">
      <button data-testid="player-like-btn" onClick={like} className="rounded-full p-2 text-slate-400 transition-colors hover:text-[#FF4500]" aria-label="Me gusta">
        <Heart className={`h-4 w-4 ${isLiked ? "fill-[#FF4500] text-[#FF4500]" : ""}`} />
      </button>
      <button data-testid="player-share-btn" onClick={share} className="rounded-full p-2 text-slate-400 transition-colors hover:text-white" aria-label="Compartir">
        <Share2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export const PlayerBar = () => {
  const { track, channel, playing, loading, toggle, skip, volume, setVolume, progress } = usePlayer();
  return (
    <div data-testid="player-bar" className="fixed bottom-0 left-0 z-50 w-full border-t border-white/10 bg-[#0A0A0E]/95 backdrop-blur-2xl">
      <div className="absolute left-0 top-0 h-[2px] bg-[#FF4500] transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
      <div className="mx-auto flex h-20 max-w-[1400px] items-center gap-3 px-4 sm:h-24 md:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-md bg-[#181824] sm:h-14 sm:w-14 ${playing ? "glow-live" : ""}`}>
            {track?.cover_url ? <img src={mediaUrl(track.cover_url)} alt="" className="h-full w-full object-cover" /> : <Disc3 className="m-auto h-full w-6 text-slate-600" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {channel ? <LiveBadge label={`EN VIVO · ${channel.name.toUpperCase()}`} /> : <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500">{track ? "REPRODUCIENDO" : "RARA PERLA RADIO"}</span>}
              {track?.show && <span data-testid="player-show-badge" className="truncate rounded-sm bg-sky-500/15 px-1.5 py-0.5 font-mono text-[10px] text-sky-300">PROGRAMA · {track.show.title.toUpperCase()}{track.show.host_name && ` · CON ${track.show.host_name.toUpperCase()}`}</span>}
              {track?.featured && <span data-testid="player-featured-badge" className="rounded-sm bg-[#FF4500]/15 px-1.5 py-0.5 font-mono text-[10px] text-[#FF7A45]">DESTACADA</span>}
            </div>
            <p data-testid="player-track-title" className="truncate font-heading text-sm font-bold text-white sm:text-base">{track ? track.title : "Elige un canal para empezar"}</p>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              {playing && <Equalizer />}
              <span data-testid="player-track-artist" className="truncate">{track?.artist_name || "Música independiente 24/7"}</span>
              {track?.artist_id && <Link data-testid="player-presskit-link" to={`/artista/${track.artist_id}`} className="shrink-0 text-[#FF7A45] underline-offset-2 hover:underline">Ver Press Kit</Link>}
            </div>
          </div>
        </div>
        {track && !track.is_message && <div className="hidden sm:block"><LikeShare track={track} /></div>}
        <div className="flex items-center gap-2">
          <button data-testid="player-play-pause-btn" onClick={toggle} disabled={!track} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF4500] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-40" aria-label={playing ? "Pausar" : "Reproducir"}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : playing ? <Pause className="h-5 w-5 fill-white" /> : <Play className="ml-0.5 h-5 w-5 fill-white" />}
          </button>
          <button data-testid="player-skip-btn" onClick={skip} disabled={!channel || !!track?.show} className="rounded-full p-2 text-slate-300 transition-colors hover:text-white disabled:opacity-30" aria-label="Saltar">
            <SkipForward className="h-5 w-5" />
          </button>
        </div>
        <div className="hidden w-36 items-center gap-2 md:flex">
          <button data-testid="player-mute-btn" onClick={() => setVolume(volume > 0 ? 0 : 0.8)} className="text-slate-400 hover:text-white" aria-label="Silenciar">
            {volume > 0 ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <Slider data-testid="player-volume-slider" value={[volume * 100]} max={100} step={1} onValueChange={([v]) => setVolume(v / 100)} />
        </div>
      </div>
    </div>
  );
};
